import React, { useMemo, useState } from 'react';
import { resolveFrontendApiBase } from '../utils/apiBase';
import { Alert, Button, Card, Descriptions, Input, InputNumber, Modal, Space, Table, Tag, Typography, message } from 'antd';

import adminAxios from './adminAxios';
import './AdminMaintenanceTools.css';

const { Text } = Typography;

const API_BASE = resolveFrontendApiBase();

type BackfillSample = {
  seriesId?: string;
  dotKeys?: string[];
  dotKeyCount?: number;
  alwaysLanguageKeys?: string[];
  neverLanguageKeys?: string[];
};

type BackfillResponse = {
  dry_run?: boolean;
  series_id?: string | null;
  scan_limit?: number | null;
  scanned?: number;
  candidates?: number;
  updated?: number;
  series_with_errors?: number;
  dotted_fields_detected?: number;
  dotted_fields_removed?: number;
  updates_applied?: boolean;
  errors?: string[];
  samples?: BackfillSample[];
};

const AdminGlossaryBackfillTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [seriesId, setSeriesId] = useState('');
  const [scanLimit, setScanLimit] = useState<number | null>(5000);
  const [result, setResult] = useState<BackfillResponse | null>(null);

  const runBackfill = async (dryRun: boolean) => {
    setLoading(true);
    try {
      const payload: Record<string, any> = {
        dry_run: dryRun,
      };
      const trimmedSeriesId = seriesId.trim();
      if (trimmedSeriesId) {
        payload.series_id = trimmedSeriesId;
      }
      if (typeof scanLimit === 'number' && Number.isFinite(scanLimit) && scanLimit > 0) {
        payload.scan_limit = Math.floor(scanLimit);
      }

      const response = await adminAxios.post<BackfillResponse>(
        `${API_BASE}/admin/backfill-series-glossary-dot-keys`,
        payload
      );
      const data = response.data || {};
      setResult(data);

      if (dryRun) {
        message.success(
          `Dry run complete. Candidates: ${Number(data.candidates || 0).toLocaleString()}, dotted fields: ${Number(
            data.dotted_fields_detected || 0
          ).toLocaleString()}.`
        );
      } else {
        message.success(
          `Backfill complete. Updated ${Number(data.updated || 0).toLocaleString()} series docs; removed ${Number(
            data.dotted_fields_removed || 0
          ).toLocaleString()} dotted fields.`
        );
      }
    } catch (error: any) {
      const detail =
        (typeof error?.response?.data?.detail === 'string' && error.response.data.detail) ||
        (typeof error?.message === 'string' && error.message) ||
        'Backfill failed';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const sampleColumns = useMemo(
    () => [
      {
        title: 'Series ID',
        dataIndex: 'seriesId',
        key: 'seriesId',
        render: (value: string) => <Text code>{value}</Text>,
      },
      {
        title: 'Dot Keys',
        dataIndex: 'dotKeys',
        key: 'dotKeys',
        render: (value: string[]) =>
          Array.isArray(value) && value.length ? (
            <Space wrap>
              {value.map((key) => (
                <Tag key={key}>{key}</Tag>
              ))}
            </Space>
          ) : (
            '—'
          ),
      },
      {
        title: 'Always Lang Keys',
        dataIndex: 'alwaysLanguageKeys',
        key: 'alwaysLanguageKeys',
        render: (value: string[]) =>
          Array.isArray(value) && value.length ? value.join(', ') : '—',
      },
      {
        title: 'Never Lang Keys',
        dataIndex: 'neverLanguageKeys',
        key: 'neverLanguageKeys',
        render: (value: string[]) =>
          Array.isArray(value) && value.length ? value.join(', ') : '—',
      },
    ],
    []
  );

  return (
    <Card title="Series Glossary Dot-Key Backfill" bordered={false} className="admin-maintenance-tool">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary" className="admin-maintenance-tool__purpose">
          Migrates legacy dotted fields like <Text code>alwaysTranslate.German</Text> into nested maps (
          <Text code>alwaysTranslate {'{'} German {'}'}</Text>), then removes dotted fields.
        </Text>
        <Alert
          className="admin-maintenance-tool__effect"
          type="warning"
          showIcon
          message="Run a dry run first"
          description="The final backfill writes series documents and removes legacy dotted fields."
        />

        <Space wrap className="admin-maintenance-tool__controls admin-maintenance-tool__actions">
          <Input
            value={seriesId}
            onChange={(event) => setSeriesId(event.target.value)}
            placeholder="Optional: target seriesId"
            style={{ width: 320 }}
            allowClear
          />
          <InputNumber
            min={1}
            max={50000}
            value={scanLimit ?? undefined}
            onChange={(value) =>
              setScanLimit(typeof value === 'number' && Number.isFinite(value) ? value : null)
            }
            placeholder="Scan limit"
            style={{ width: 160 }}
          />
          <Button loading={loading} onClick={() => runBackfill(true)}>
            Dry Run
          </Button>
          <Button
            type="primary"
            danger
            loading={loading}
            onClick={() => {
              Modal.confirm({
                title: 'Run glossary dot-key backfill?',
                content:
                  'This will update series docs and remove legacy dotted fields. Run a dry run first if you have not already.',
                okText: 'Run Backfill',
                okButtonProps: { danger: true },
                onOk: () => runBackfill(false),
              });
            }}
          >
            Run Backfill
          </Button>
        </Space>

        {result && (
          <div className="admin-maintenance-tool__result-panel">
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Dry Run">{result.dry_run ? 'Yes' : 'No'}</Descriptions.Item>
              <Descriptions.Item label="Updates Applied">
                {result.updates_applied ? 'Yes' : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="Scanned">
                {Number(result.scanned || 0).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Candidates">
                {Number(result.candidates || 0).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Updated">
                {Number(result.updated || 0).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Series Errors">
                {Number(result.series_with_errors || 0).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Dotted Fields Detected">
                {Number(result.dotted_fields_detected || 0).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Dotted Fields Removed">
                {Number(result.dotted_fields_removed || 0).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`Errors (${result.errors.length})`}
                description={
                  <div style={{ maxHeight: 180, overflow: 'auto' }}>
                    {result.errors.slice(0, 20).map((err, idx) => (
                      <div key={`${idx}-${err}`}>{err}</div>
                    ))}
                  </div>
                }
              />
            )}

            <Table<BackfillSample>
              rowKey={(record, index) => `${record.seriesId || 'series'}-${index}`}
              columns={sampleColumns}
              dataSource={Array.isArray(result.samples) ? result.samples : []}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 860 }}
              size="small"
            />
          </div>
        )}
      </Space>
    </Card>
  );
};

export default AdminGlossaryBackfillTab;
